import {isFailedToolResult, parseToolResult} from "@/components/ask/toolCallUtils";

export type ToolApprovalStatus = "submitting" | "denied" | "approved" | "completed" | "reviewed" | "indeterminate" | "unavailable" | "required" | "failed";

/**
 * Wingman reports an approved Gail mutation that did not execute as
 * `{status: "rejected" | "indeterminate", unknown, reason}` rather than as a tool error.
 * A rejection (including a stale preview) changed nothing; an indeterminate one may have.
 */
function executionStatus(result: Record<string, unknown> | null): ToolApprovalStatus {
   if (result?.status === "rejected") return "failed";
   if (result?.status === "indeterminate" || result?.unknown === true) return "indeterminate";
   return isFailedToolResult(result) ? "failed" : "completed";
}

export function resolveToolApprovalStatus(
   pending: boolean,
   approved: boolean | undefined,
   completed: boolean,
   active: boolean,
   result?: unknown
): ToolApprovalStatus {
   if (approved === false) return "denied";
   if (completed) return executionStatus(parseToolResult(result));
   if (pending) return "submitting";
   if (approved === true) return "approved";
   if (!active) return "unavailable";
   return "required";
}

/** One label for a batch of proposals. A mix of applied and canceled proposals is reviewed. */
export function combinedApprovalStatus(statuses: ToolApprovalStatus[], pending: boolean): ToolApprovalStatus {
   if (statuses.includes("failed")) return "failed";
   if (statuses.includes("indeterminate")) return "indeterminate";
   if (statuses.every(value => value === "completed")) return "completed";
   if (pending) return "submitting";
   if (statuses.includes("required")) return "required";
   if (statuses.includes("approved")) return "approved";
   if (statuses.every(value => value === "denied")) return "denied";
   if (statuses.every(value => value === "completed" || value === "denied")) return "reviewed";
   return "unavailable";
}
